// Copyright 2026 FlairCV Contributors
// SPDX-License-Identifier: AGPL-3.0-or-later

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import type { EducationFormData } from '../types/schemas';

const EDUCATION_QUERY_KEY = ['education'] as const;
const CREDITS_QUERY_KEY = ['credits'] as const;

/** Hook for fetching all education entries. */
export function useEducation() {
  return useQuery({
    queryKey: EDUCATION_QUERY_KEY,
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/api/v1/education');

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
  });
}

/** Hook for creating a new education entry. */
export function useCreateEducation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: EducationFormData & { is_certification?: boolean }) => {
      const { data, error } = await apiClient.POST('/api/v1/education', {
        body: {
          institution: params.institution,
          degree: params.degree || undefined,
          field_of_study: params.field_of_study || undefined,
          start_date: params.start_date || undefined,
          end_date: params.end_date || undefined,
          gpa: params.gpa || undefined,
          description: params.description || undefined,
          is_certification: params.is_certification || undefined,
        },
      });

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EDUCATION_QUERY_KEY });
    },
  });
}

/** Hook for updating an existing education entry. */
export function useUpdateEducation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...params
    }: EducationFormData & { id: string; is_certification?: boolean }) => {
      const { data, error } = await apiClient.PUT(
        '/api/v1/education/{id}',
        {
          params: { path: { id } },
          body: {
            institution: params.institution,
            degree: params.degree || undefined,
            field_of_study: params.field_of_study || undefined,
            start_date: params.start_date || undefined,
            end_date: params.end_date || undefined,
            gpa: params.gpa || undefined,
            description: params.description || undefined,
            is_certification: params.is_certification,
          },
        },
      );

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EDUCATION_QUERY_KEY });
    },
  });
}

/** Hook for deleting an education entry. */
export function useDeleteEducation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await apiClient.DELETE(
        '/api/v1/education/{id}',
        { params: { path: { id } } },
      );

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EDUCATION_QUERY_KEY });
    },
  });
}

/** Hook for bulk deleting education entries. */
export function useBulkDeleteEducation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      const { data, error } = await apiClient.POST(
        '/api/v1/education/bulk-delete',
        { body: { ids } },
      );

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EDUCATION_QUERY_KEY });
    },
  });
}

/**
 * AI-polish (or draft) an education description. Pass `id` alone to polish
 * the saved entry; pass inline fields to polish what is currently in the form.
 */
export function usePolishEducation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      institution,
      degree,
      field_of_study,
      description,
      is_certification,
    }: {
      id?: string;
      institution?: string;
      degree?: string;
      field_of_study?: string;
      description?: string;
      is_certification?: boolean;
    }) => {
      const pathId = id || '00000000-0000-0000-0000-000000000000';
      const inline =
        institution !== undefined ||
        degree !== undefined ||
        field_of_study !== undefined ||
        description !== undefined;
      const { data, error } = await apiClient.POST('/api/v1/education/{id}/polish', {
        params: { path: { id: pathId } },
        body: inline
          ? {
              institution: institution ?? '',
              degree: degree ?? '',
              field_of_study: field_of_study ?? '',
              description: description ?? '',
              is_certification: is_certification ?? false,
            }
          : {},
      });

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CREDITS_QUERY_KEY });
    },
  });
}
